import React, { useMemo, useCallback } from 'react';
import moment from 'moment';

import { Agent } from '~/ui/assets/icons';
import { Media } from '~/ui/components/Media/Media';
import { ContentMedia } from '~/ui/components/ContentMedia/ContentMedia';
import { MessageReaction } from './MessageReaction';
import { MessageLocation } from './MessageLocation';
import { MessageList } from './MessageList';
import { MessageListReply } from './MessageListReply';
import { MessageButtonReply } from './MessageButtonReply';
import { MessageTemplate } from './MessageTemplate';

import {
  AgentCard,
  Icon,
  Info,
  ClientCard,
  Position,
  CardWrapper,
  Date,
  Name,
  Text,
  SystemNoteText,
  DateCard,
  TextWrapper,
  MessageWrapper,
  DateWrapper,
} from './Conversation.styled';

export type MessageType = {
  type?: string;
  body: string;
  index: number | string;
  author: string;
  media: Array<{
    category: string;
    size: number;
    filename: string;
    contentType: string;
    sid: string;
  }>;
  content?: {
    media_url: string;
    storage_key: string;
    content_type: string;
    filename?: string;
    emoji?: string;
    latitude?: number;
    longitude?: number;
    name?: string;
    address?: string;
    sections?: Array<{
      title?: string;
      rows: Array<{ id: string; title: string; description?: string }>;
    }>;
    listReplyTitle?: string;
    buttonReplyTitle?: string;
    templateName?: string;
    templateParams?: string[];
  };
  authorType: string;
  dateUpdated: string;
  dateCreated: string;
  isSystemNote?: boolean;
};

type RenderConversationProps = {
  messages: MessageType[];
  conversationId: string;
};

function formatPhoneSmart(input: string | null | undefined): string {
  if (!input?.length) return '';

  const raw = input.trim().replace(/^[a-zA-Z]+:/, '');
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  const dash = ' - ';
  const hadPlus = raw.includes('+');

  const isBRWithCC = digits.startsWith('55') && (digits.length === 12 || digits.length === 13);

  if (isBRWithCC) {
    const nsn = digits.slice(2);
    const area = nsn.slice(0, 2);
    const local = nsn.slice(2);

    if (!area) return `${dash}+55`;
    if (!local) return `${dash}+55 ${area}`;

    let left = local.slice(0, Math.max(local.length - 4, 1));
    let right = local.slice(-4);

    if (local.length === 9) {
      left = local.slice(0, 5);
      right = local.slice(5);
    } else if (local.length === 8) {
      left = local.slice(0, 4);
      right = local.slice(4);
    }

    return `${dash}+55 ${area} ${left}-${right}`;
  }

  return dash + (hadPlus ? `+${digits}` : digits);
}
export function RenderConversation({ messages, conversationId }: RenderConversationProps) {
  const isItBot = ['SYSTEM', 'BOT'];

  const formatDate = (date: string, format = 'DD [de] MMM [de] YYYY') => moment(date).format(format);
  const formatHour = (date: string, format = 'HH:mm') => moment(date).format(format);

  const renderDate = useCallback(
    (line: MessageType, prevDate: string) =>
      formatDate(line?.dateCreated) !== prevDate ? (
        <DateWrapper marginTop={!!prevDate}>
          <DateCard>
            <Date>{formatDate(line?.dateCreated)}</Date>
          </DateCard>
        </DateWrapper>
      ) : null,
    [],
  );

  const agentIcon = useMemo(
    () => (
      <Icon>
        <Agent />
      </Icon>
    ),
    [],
  );

  const renderAuthorName = (line: MessageType) => {
    if (isItBot.includes(line?.authorType) && line.author.toLowerCase() === 'gpt') {
      return line?.author;
    }
    if (isItBot.includes(line?.authorType)) {
      return 'Auto Atendimento';
    }
    return line?.author;
  };

  const handleGetAuthor = (msg: MessageType) => {
    if (msg?.authorType === 'CLIENT') return 'client';
    if (msg?.author.toLowerCase() === 'gpt') return 'gpt';
    return 'agent';
  };

  return (
    <>
      {messages.map((line, index) => {
        const prevMessage = messages[index - 1];
        const nextMessage = messages[index + 1];

        const prevMessageFrom = handleGetAuthor(prevMessage);
        const currentMessageFrom = handleGetAuthor(line);

        const prevDate = formatDate(prevMessage?.dateCreated);

        const isDateChanged =
          !prevMessage?.dateCreated || moment(line.dateCreated).diff(moment(prevMessage.dateCreated), 'minutes') >= 1;

        const isAuthorChanged = prevMessageFrom !== currentMessageFrom;

        const shouldShowInfo = isDateChanged || isAuthorChanged;

        const hasIcon = nextMessage?.authorType === 'CLIENT';

        const isWhatsAppMessage = line?.author?.includes('whatsapp:+');

        if (currentMessageFrom === 'client') {
          return (
            <React.Fragment key={line.index}>
              {renderDate(line, prevDate)}
              <Position justifyContent="start">
                <CardWrapper
                  alignItems="start"
                  fullWidth={!!(line?.media.length > 0 && line?.media[0]?.contentType.includes('audio'))}
                >
                  {shouldShowInfo && (
                    <Info>
                      <Name>Cliente {isWhatsAppMessage ? formatPhoneSmart(line?.author) : null}</Name>
                      <Date hour>{formatHour(line?.dateCreated)}</Date>
                    </Info>
                  )}
                  <ClientCard marginTop={!shouldShowInfo}>
                    {line.type === 'reaction' && <MessageReaction emoji={line.content?.emoji} />}
                    {line.type === 'location' && (
                      <MessageLocation
                        name={line.content?.name}
                        address={line.content?.address}
                        latitude={line.content?.latitude}
                        longitude={line.content?.longitude}
                      />
                    )}
                    {line.type === 'list' && <MessageList body={line.body} sections={line.content?.sections} />}
                    {line.type === 'list_reply' && <MessageListReply title={line.content?.listReplyTitle} />}
                    {line.type === 'button_reply' && <MessageButtonReply title={line.content?.buttonReplyTitle} />}
                    {line.type === 'template' && (
                      <MessageTemplate name={line.content?.templateName} params={line.content?.templateParams} />
                    )}
                    {!['reaction', 'location', 'list', 'list_reply', 'button_reply', 'template'].includes(line.type) &&
                      line.body &&
                      (line.isSystemNote ? (
                        <SystemNoteText>{line.body}</SystemNoteText>
                      ) : (
                        <Text weight={400}>{line.body}</Text>
                      ))}
                    {line.media?.length > 0 && <Media media={line.media[0]} conversationId={conversationId} />}
                    {line.content?.media_url && <ContentMedia content={line.content} />}
                  </ClientCard>
                </CardWrapper>
              </Position>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment key={line.index}>
            {renderDate(line, prevDate)}
            <Position justifyContent="end">
              <CardWrapper alignItems="end" marginTop={!isDateChanged && isAuthorChanged}>
                <MessageWrapper hasIcon={hasIcon}>
                  <TextWrapper>
                    {shouldShowInfo && (
                      <Info>
                        <Name>{renderAuthorName(line)}</Name>
                        <Date hour>{formatHour(line?.dateCreated)}</Date>
                      </Info>
                    )}
                    <AgentCard marginTop={!shouldShowInfo}>
                      {line.type === 'reaction' && <MessageReaction emoji={line.content?.emoji} />}
                      {line.type === 'location' && (
                        <MessageLocation
                          name={line.content?.name}
                          address={line.content?.address}
                          latitude={line.content?.latitude}
                          longitude={line.content?.longitude}
                        />
                      )}
                      {line.type === 'list' && <MessageList body={line.body} sections={line.content?.sections} />}
                      {line.type === 'list_reply' && <MessageListReply title={line.content?.listReplyTitle} />}
                      {line.type === 'button_reply' && <MessageButtonReply title={line.content?.buttonReplyTitle} />}
                      {line.type === 'template' && (
                        <MessageTemplate name={line.content?.templateName} params={line.content?.templateParams} />
                      )}
                      {!['reaction', 'location', 'list', 'list_reply', 'button_reply', 'template'].includes(
                        line.type,
                      ) &&
                        line.body &&
                        (line.isSystemNote ? (
                          <SystemNoteText>{line.body}</SystemNoteText>
                        ) : (
                          <Text weight={400}>{line.body}</Text>
                        ))}
                      {line.media?.length > 0 && <Media media={line.media[0]} conversationId={conversationId} />}
                      {line.content?.media_url && <ContentMedia content={line.content} />}
                    </AgentCard>
                  </TextWrapper>
                  {hasIcon && agentIcon}
                </MessageWrapper>
              </CardWrapper>
            </Position>
          </React.Fragment>
        );
      })}
    </>
  );
}
